/**
 * ### attachMentionBadgeHandlers
 *
 * @description
 * Attaches click event handlers to mention badge close buttons within a span element.
 * When a close button is clicked, it removes the badge and triggers an input event.
 *
 * @example
 * attachMentionBadgeHandlers(spanRef.current);
 *
 * @param spanElement - The span element containing mention badges
 * @returns void
 */
const attachMentionBadgeHandlers = (spanElement?: HTMLSpanElement | null): void => {
  if (spanElement) {
    const closeButtons = spanElement.querySelectorAll('.mention-badge span');
    closeButtons.forEach((btn) => {
      const badge = btn.parentElement;
      if (badge?.classList.contains('mention-badge')) {
        attachMentionBadgeHandler(btn as HTMLElement, badge, spanElement);
      }
    });
  }
};

/**
 * ### attachMentionBadgeHandler
 *
 * @description
 * Attaches a click event handler to a single mention badge close button.
 * When clicked, it removes the badge and triggers an input event.
 *
 * @example
 * attachMentionBadgeHandler(closeBtn, badge, spanRef.current);
 *
 * @param closeButton - The close button element
 * @param badge - The badge element to remove
 * @param spanElement - The span element to dispatch the input event on
 * @returns void
 */
const attachMentionBadgeHandler = (
  closeButton: HTMLElement,
  badge: HTMLElement,
  spanElement: HTMLSpanElement,
): void => {
  closeButton.addEventListener('click', (e) => {
    e.stopPropagation();
    badge.remove();
    // Trigger input event to update value
    const event = new Event('input', { bubbles: true });
    spanElement.dispatchEvent(event);
  });
};

export default attachMentionBadgeHandlers;
export { attachMentionBadgeHandler };
